import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle, ShieldCheck, MapPin } from "lucide-react";
import { WHATSAPP_LINK, scrollToId } from "@/lib/site";

const LINES = [
  { text: "Spotless homes.", color: "text-[#FBF9F5]" },
  { text: "Green gardens.", color: "text-[#FBF9F5]" },
  { text: "Fresh paint.", color: "text-[#D4A359]" },
];

const HERO_VIDEO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_kede-services/artifacts/6b0e6e44825c6438_WhatsApp%20Video%202026-10-08%20at%2015.57.42.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1752836720470-5647462f707b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxjb2FzdGFsJTIwdmlsbGElMjBpbnRlcmlvciUyMGNsZWFuJTIwbGl2aW5nJTIwcm9vbSUyMEtlbnlhJTIwc3Vubnl8ZW58MHx8fHwxNzkyNzQyMzc1fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVideo = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: yVideo }} className="absolute inset-0 scale-110">
        <video
          data-testid="hero-background-video"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          muted
          loop
          autoPlay
          playsInline
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0E1F16]/90 via-[#132A1F]/70 to-[#132A1F]/35" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0E1F16]/80 to-transparent" />

      <motion.div style={{ y: yContent, opacity: fade }} className="relative mx-auto w-full max-w-7xl px-5 pt-28 pb-20 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-jbm text-xs font-semibold uppercase tracking-[0.3em] text-[#D4A359]"
        >
          Shanzu · Mombasa North Coast · Kenya
        </motion.p>

        <h1 className="font-display mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.color}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.18 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                style={{ textShadow: "0 4px 30px rgba(0,0,0,0.45)" }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-[#E2EFEA]/90 sm:text-lg"
        >
          KeDe-Ceaning is Shanzu's precision property-care crew — deep cleaning, streak-free windows,
          lawn mowing &amp; tropical gardening, interior and exterior painting, and all the odd repairs in
          between. For private homes and businesses alike.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-quote-button"
            onClick={() => scrollToId("#quote")}
            className="group flex items-center gap-2 rounded-full bg-[#C85A32] px-7 py-4 text-sm font-semibold text-white shadow-[0_18px_40px_-12px_rgba(200,90,50,0.7)] transition-[background-color,transform] duration-300 hover:bg-[#b34e2b] hover:scale-[1.04]"
          >
            Request a Free Quote
            <ArrowDownRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>
          <a
            data-testid="hero-cta-whatsapp-button"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-2 rounded-full border-2 border-[#25D366] bg-[#25D366]/15 px-7 py-[14px] text-sm font-semibold text-white backdrop-blur-sm transition-[background-color,transform] duration-300 hover:bg-[#25D366]/35 hover:scale-[1.04]"
          >
            <MessageCircle size={17} className="text-[#25D366] transition-transform duration-300 group-hover:-rotate-12" />
            WhatsApp Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-[#E2EFEA]/70"
        >
          <span className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#D4A359]" /> Homes &amp; businesses
          </span>
          <span className="flex items-center gap-2">
            <MapPin size={16} className="text-[#D4A359]" /> Shanzu · Bamburi · Nyali · Mtwapa
          </span>
          <span className="font-jbm text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E2EFEA]/60">
            Trusted in Shanzu since 2018
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-[#FBF9F5]/40 p-1.5"
        >
          <div className="h-2 w-1 rounded-full bg-[#FBF9F5]/70" />
        </motion.div>
      </motion.div>
    </section>
  );
}
